// Silo Art — Stage 2 ("Elevated Mode") control panel.
// Lives in the second tab of a silo's Silo Art studio. The zone LOOK (content
// canvas + header band) is stored PER-SILO and now edited with an explicit
// Save button (local edits until you press Save). The leader strategy,
// bold-loose-text flag and per-feature-page opt-outs are global (per creator).
// Each zone can PULL a flat colour from the art (eyedropper w/ zoom loupe) or
// LOCK a favourite cube face as its image.
import { useEffect, useRef, useState } from "react";
import { Eyedropper, Sparkle, Copy, LockSimple, LockSimpleOpen, FloppyDisk, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useSiloArt, SiloArtSurface } from "@/components/SiloArtLayer";
import { FILTERS, faceImageUrl, normalizeZones, FACE_LABELS } from "@/lib/siloArt";
import { api } from "@/lib/api";

const ZONE_DEFAULT_FACE = { page: "center", band: "users" };
const SRC_LABEL = { image: "Image", color: "Flat colour", off: "Off" };
const toHex = (r, g, b) => "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");

function availableFaces(silo) {
  const faces = (silo && silo.cube && silo.cube.faces) || {};
  return Object.keys(faces).filter((k) => faces[k] && faces[k].image_id);
}

function ZoneControls({ title, zoneKey, zone: z, onChange, silo, defaultFace, allowOff }) {
  const [picking, setPicking] = useState(false);   // eyedropper
  const [locking, setLocking] = useState(false);    // lock-favourite grid
  const [pickFace, setPickFace] = useState(defaultFace);
  const [ready, setReady] = useState(false);
  const [hover, setHover] = useState(null);
  const [loupe, setLoupe] = useState(null);
  const canvasRef = useRef(null);
  const loupeRef = useRef(null);

  const faces = availableFaces(silo);
  const effectivePickFace = faces.includes(pickFace) ? pickFace : (faces[0] || defaultFace);
  const pickUrl = faceImageUrl(silo, effectivePickFace);
  const lockedFace = z.face && faces.includes(z.face) ? z.face : null;
  const previewFace = lockedFace || defaultFace;
  const previewUrl = faceImageUrl(silo, previewFace);

  const update = (patch) => onChange({ ...z, ...patch });

  const openPicker = () => {
    if (!faces.length) return;
    setLocking(false);
    setPickFace(faces.includes(defaultFace) ? defaultFace : faces[0]);
    setHover(null); setReady(false); setPicking(true);
  };
  const closePicker = () => { setPicking(false); setHover(null); setLoupe(null); };

  useEffect(() => {
    if (!picking || !pickUrl) return;
    setReady(false);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const c = canvasRef.current;
      if (!c) return;
      const w = 320;
      c.width = w;
      c.height = Math.max(1, Math.round((img.naturalHeight / img.naturalWidth) * w));
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      setReady(true);
    };
    img.onerror = () => setReady(false);
    img.src = pickUrl;
  }, [picking, pickUrl]);

  const sampleAt = (e) => {
    const c = canvasRef.current;
    if (!c || !ready) return null;
    const rect = c.getBoundingClientRect();
    const x = Math.max(0, Math.min(c.width - 1, Math.round(((e.clientX - rect.left) / rect.width) * c.width)));
    const y = Math.max(0, Math.min(c.height - 1, Math.round(((e.clientY - rect.top) / rect.height) * c.height)));
    try {
      const [r, g, b] = c.getContext("2d").getImageData(x, y, 1, 1).data;
      return { x, y, rect, hex: toHex(r, g, b) };
    } catch { return null; }
  };

  const onCanvasMove = (e) => {
    const s = sampleAt(e);
    if (!s) return;
    setHover({ hex: s.hex });
    setLoupe({ left: e.clientX - s.rect.left + 14, top: e.clientY - s.rect.top + 14 });
    const lc = loupeRef.current;
    if (lc) {
      const ctx = lc.getContext("2d");
      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, lc.width, lc.height);
      const win = 11;
      ctx.drawImage(canvasRef.current, s.x - (win - 1) / 2, s.y - (win - 1) / 2, win, win, 0, 0, lc.width, lc.height);
      const cell = lc.width / win;
      ctx.strokeStyle = "rgba(0,0,0,0.75)"; ctx.lineWidth = 2;
      ctx.strokeRect(((win - 1) / 2) * cell, ((win - 1) / 2) * cell, cell, cell);
    }
  };

  const onCanvasClick = (e) => {
    const s = sampleAt(e);
    if (!s) return;
    update({ color: s.hex, source: "color", face: null });
    closePicker();
  };

  const lockThisFace = (f) => { update({ face: f, source: "image" }); setLocking(false); };

  const sources = allowOff ? ["image", "color", "off"] : ["image", "color"];
  const previewDescriptor = { source: z.source, filter: z.filter, dim: z.dim, color: z.color, imageUrl: previewUrl };

  return (
    <div className="border hairline rounded-lg p-4 bg-[#FDFCF8]" data-testid={`v2-zone-${zoneKey}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-serif-display text-base text-[#1C1B1A]">{title}</h4>
        <div className="flex rounded-md overflow-hidden border hairline text-[11px]">
          {sources.map((s) => (
            <button key={s} onClick={() => update({ source: s })} data-testid={`v2-${zoneKey}-source-${s}`}
              className={`px-3 py-1 ${z.source === s ? "bg-[#1C1B1A] text-[#FDFCF8]" : "bg-white text-[#6B6A68]"}`}>
              {SRC_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      {z.source === "off" ? (
        <p className="text-xs text-[#6B6A68]">This zone stays plain — no art.</p>
      ) : (
        <>
          <div className="relative h-28 rounded-lg overflow-hidden border hairline mb-3">
            <SiloArtSurface descriptor={previewDescriptor} dark={false} className="absolute inset-0 siloart-canvas-truecolor" testid={`v2-preview-${zoneKey}`} />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs px-2 py-1 rounded bg-black/30 text-white backdrop-blur-sm">Sample text legibility</span>
            </div>
            {z.source === "image" && lockedFace && (
              <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-black/55 text-white flex items-center gap-1" data-testid={`v2-${zoneKey}-locked-badge`}>
                <LockSimple size={11} weight="fill" /> {FACE_LABELS[lockedFace] || lockedFace}
              </span>
            )}
          </div>

          {z.source === "image" && (
            <div className="mb-3">
              <div className="text-[11px] uppercase tracking-[0.14em] text-[#6B6A68] mb-1.5">Filter</div>
              <div className="flex flex-wrap gap-1.5">
                {FILTERS.map((f) => (
                  <button key={f.id} onClick={() => update({ filter: f.id })} data-testid={`v2-${zoneKey}-filter-${f.id}`}
                    className={`text-[11px] px-2.5 py-1 rounded-md border ${z.filter === f.id ? "bg-[#4A5D4E] text-white border-[#4A5D4E]" : "bg-white text-[#4A5D4E] border-transparent hover:border-[#A7B3A0]"}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mb-3">
            <div className="flex items-center justify-between text-[11px] text-[#6B6A68] mb-1">
              <span className="uppercase tracking-[0.14em]">Dim / readability</span>
              <span className="tabular-nums">{z.dim}%</span>
            </div>
            <input type="range" min="0" max="85" value={z.dim} onChange={(e) => update({ dim: Number(e.target.value) })}
              data-testid={`v2-${zoneKey}-dim`} className="w-full accent-[#4A5D4E]" />
          </div>

          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => (picking ? closePicker() : openPicker())}
                disabled={!faces.length}
                data-testid={`v2-${zoneKey}-eyedropper`}
                className={`text-[11px] px-2.5 py-1.5 rounded-md border hairline flex items-center gap-1.5 disabled:opacity-40 ${picking ? "bg-[#4A5D4E] text-white border-[#4A5D4E]" : "text-[#4A5D4E]"}`}
              >
                <Eyedropper size={13} /> {picking ? "Done" : "Pull a colour"}
              </button>
              {z.source === "color" && (
                <>
                  <span className="inline-block w-6 h-6 rounded border hairline" style={{ backgroundColor: z.color }} title={z.color} data-testid={`v2-${zoneKey}-swatch`} />
                  <span className="text-[11px] text-[#6B6A68] tabular-nums">{z.color}</span>
                </>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => { setPicking(false); setLocking((v) => !v); }}
                disabled={!faces.length}
                data-testid={`v2-${zoneKey}-lock`}
                className={`text-[11px] px-2.5 py-1.5 rounded-md border hairline flex items-center gap-1.5 disabled:opacity-40 ${locking ? "bg-[#4A5D4E] text-white border-[#4A5D4E]" : "text-[#4A5D4E]"}`}
              >
                <LockSimple size={13} /> Lock favourite
              </button>
              {z.source === "image" && lockedFace && (
                <button onClick={() => update({ face: null })} data-testid={`v2-${zoneKey}-unlock`}
                  className="text-[11px] px-2.5 py-1.5 rounded-md border hairline text-[#6B6A68] flex items-center gap-1.5 hover:text-[#1C1B1A]">
                  <LockSimpleOpen size={13} /> Unlock ({FACE_LABELS[lockedFace] || lockedFace})
                </button>
              )}
            </div>
          </div>

          {picking && (
            <div className="mt-3 p-3 border hairline rounded-lg bg-white" data-testid={`v2-${zoneKey}-picker`}>
              <div className="flex items-center justify-between mb-2 gap-2">
                <p className="text-[11px] text-[#6B6A68]">Move over your art, then tap the exact spot.</p>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="inline-block w-5 h-5 rounded border hairline" style={{ backgroundColor: hover ? hover.hex : z.color }} data-testid={`v2-${zoneKey}-hover-swatch`} />
                  <span className="text-[11px] tabular-nums text-[#6B6A68] w-[58px]">{hover ? hover.hex : "—"}</span>
                </div>
              </div>
              {faces.length > 1 && (
                <div className="flex gap-1.5 overflow-x-auto pb-2 mb-2" data-testid={`v2-${zoneKey}-faces`}>
                  {faces.map((f) => (
                    <button key={f} onClick={() => { setReady(false); setPickFace(f); }} title={FACE_LABELS[f] || f}
                      data-testid={`v2-${zoneKey}-pickface-${f}`}
                      className={`shrink-0 w-11 h-11 rounded-md overflow-hidden border-2 ${effectivePickFace === f ? "border-[#4A5D4E]" : "border-transparent hover:border-[#A7B3A0]"}`}>
                      <img src={faceImageUrl(silo, f)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
              <div className="relative inline-block max-w-full">
                {!ready && <p className="text-[11px] text-[#6B6A68] py-8 text-center">Loading art…</p>}
                <canvas ref={canvasRef} onClick={onCanvasClick} onMouseMove={onCanvasMove}
                  onMouseLeave={() => { setHover(null); setLoupe(null); }}
                  data-no-invert
                  className={`cursor-crosshair rounded-md block ${ready ? "" : "hidden"}`}
                  style={{ maxHeight: 260, maxWidth: "100%" }} data-testid={`v2-${zoneKey}-canvas`} />
                {ready && loupe && (
                  <div className="absolute pointer-events-none rounded-full overflow-hidden border-2 border-white shadow-lg"
                    style={{ left: loupe.left, top: loupe.top, width: 66, height: 66 }}>
                    <canvas ref={loupeRef} width={66} height={66} data-no-invert className="block" />
                  </div>
                )}
              </div>
            </div>
          )}

          {locking && (
            <div className="mt-3 p-3 border hairline rounded-lg bg-white" data-testid={`v2-${zoneKey}-lockgrid`}>
              <p className="text-[11px] text-[#6B6A68] mb-2">Pick a cube face to lock as this zone's background. The dim slider still applies.</p>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {faces.map((f) => (
                  <button key={f} onClick={() => lockThisFace(f)} title={FACE_LABELS[f] || f}
                    data-testid={`v2-${zoneKey}-lockface-${f}`}
                    className={`relative aspect-square rounded-md overflow-hidden border-2 ${lockedFace === f ? "border-[#4A5D4E]" : "border-transparent hover:border-[#A7B3A0]"}`}>
                    <img src={faceImageUrl(silo, f)} alt="" className="w-full h-full object-cover" />
                    <span className="absolute bottom-0 inset-x-0 text-[8px] leading-tight text-white bg-black/45 px-1 py-0.5 truncate">{FACE_LABELS[f] || f}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function SiloArtStage2Panel({ silo, onSaved }) {
  const { config, setConfig, silos, leaderSilo, refreshSilos, mode } = useSiloArt();
  const [zones, setZones] = useState(() => normalizeZones(silo && silo.silo_art));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  // Oct 2026 (Randy, HOMEPAGE_PRICING_REVAMP.md §9) — elevated text colours
  // only bite when there is a picture to read them against. If this silo has
  // no dressed face and no painted canvas, say so plainly instead of letting
  // the creator flip White text and watch nothing happen (or worse, watch it
  // happen on a bare cream page). The saved setting is left exactly as it is.
  const faceMap = (silo && silo.cube && silo.cube.faces) || {};
  const anyFaceDressed = Object.values(faceMap).some((f) => f && f.image_id);
  // Judged on the SAVED art, not the unsaved editor state: picking "Flat
  // colour" and not saving it would otherwise hide the notice while the pages
  // are still suspended.
  const canvasPainted = normalizeZones(silo && silo.silo_art).page.source === "color";

  // Re-sync from the silo whenever it changes OR its saved art content changes,
  // so reopening the studio after a save never shows stale zones.
  useEffect(() => { setZones(normalizeZones(silo && silo.silo_art)); setDirty(false); }, [silo && silo.id, silo && JSON.stringify(silo.silo_art || {})]); // eslint-disable-line react-hooks/exhaustive-deps

  const setZone = (key, z) => { setZones((prev) => ({ ...prev, [key]: z })); setDirty(true); };

  const save = async () => {
    if (!silo) return;
    setSaving(true);
    try {
      await api.patch(`/silos/${silo.id}`, { silo_art: { zones } });
      if (refreshSilos) refreshSilos();
      if (onSaved) onSaved();
      setDirty(false);
      toast.success("Silo art saved.");
    } catch { toast.error("Couldn't save. Try again."); }
    setSaving(false);
  };

  const applyToAll = async () => {
    setApplying(true);
    try {
      const r = await api.post("/silos/apply-art-to-all", { zones });
      if (refreshSilos) refreshSilos();
      if (onSaved) onSaved();
      setDirty(false);
      toast.success(`Look applied to ${r?.data?.updated ?? "all"} silos.`);
    } catch { toast.error("Couldn't apply to all silos."); }
    setApplying(false);
  };

  const setLeaderMode = (lm) => setConfig({ ...config, leader_mode: lm });
  const setPin = (id) => setConfig({ ...config, leader_mode: "pinned", leader_pin_silo_id: id });
  const toggleBold = () => setConfig({ ...config, bold_unboxed: !config.bold_unboxed });
  const toggleBoldMobile = () => setConfig({ ...config, bold_unboxed_mobile: !config.bold_unboxed_mobile });
  const toggleBlack = () => setConfig({
    ...config,
    black_text: !config.black_text,
    // Feb 2026 (Randy pt-siloart-white-text) — mutual exclusion: text
    // can't be both pure black and pure white, so enabling one clears
    // the corresponding same-scope white toggle.
    ...(!config.black_text ? { white_text: false } : {}),
  });
  const toggleBlackMobile = () => setConfig({
    ...config,
    black_text_mobile: !config.black_text_mobile,
    ...(!config.black_text_mobile ? { white_text_mobile: false } : {}),
  });
  const toggleWhite = () => setConfig({
    ...config,
    white_text: !config.white_text,
    ...(!config.white_text ? { black_text: false } : {}),
  });
  const toggleWhiteMobile = () => setConfig({
    ...config,
    white_text_mobile: !config.white_text_mobile,
    ...(!config.white_text_mobile ? { black_text_mobile: false } : {}),
  });
  const toggleDarkIgnore = () => setConfig({ ...config, dark_ignore_canvas: !(config.dark_ignore_canvas !== false) });
  const pageOn = (k) => (config.pages ? config.pages[k] !== false : true);
  const togglePage = (k) => setConfig({ ...config, pages: { ...(config.pages || {}), [k]: !pageOn(k) } });
  const setPageDim = (k, v) => { setZones((prev) => ({ ...prev, page_dim: { ...(prev.page_dim || {}), [k]: v } })); setDirty(true); };
  // Jul 2026 (Randy) — per-page canvas effects (art zoom / text size /
  // text colour / hide text) for the two feature pages.
  const setPageFx = (k, patch) => {
    setZones((prev) => ({
      ...prev,
      page_fx: { ...(prev.page_fx || {}), [k]: { ...((prev.page_fx || {})[k] || {}), ...patch } },
    }));
    setDirty(true);
  };

  return (
    <div className="space-y-5" data-testid="silo-art-stage2">
      {/* Sticky save bar — pinned FIRST so it locks to the very top of the
          scroll area (Randy Jul 25, 2026). One row: status + green
          save-all-silos + black save-this-silo.

          Iter160m (Randy pt-siloart-savebtn-ux, Feb 2026) — Randy: "Solo
          save button looks grayed out, doesn't feel like it's working."
          Root cause: config toggles (whitetext, bolden, etc.) auto-save
          via SiloArtLayer.setConfig, so `dirty` stays false and the
          zone-scoped Save button stayed disabled. That's technically
          correct (no zones changed = nothing new to persist) but visually
          reads as "broken." Two-part fix:
            (1) When !dirty, the Save button is now a subtle "✓ Saved"
                pill instead of a grayed-out disabled button — clearly
                communicates the save-succeeded state.
            (2) A `title` tooltip on both buttons explains scope: Solo
                Save persists the per-page BACKGROUNDS/ART CHOICES,
                Save-all-silos COPIES those choices to every other silo
                you own. The layer-level toggles (Bolden, White text)
                auto-save when you flip them, so no button needed. */}
      <div className="sticky top-0 z-20 -mx-5 sm:-mx-8 px-5 sm:px-8 py-2.5 bg-[#FDFCF8] border-b hairline flex items-center justify-between gap-2 flex-nowrap !mt-0" data-testid="v2-savebar">
        <span className="text-xs flex items-center gap-1.5 min-w-0 shrink overflow-hidden" data-testid="v2-save-status">
          {dirty
            ? <span className="text-[#C16E5A] font-medium whitespace-nowrap truncate">● Unsaved zone/art changes</span>
            : <span className="text-[#4A5D4E] flex items-center gap-1 whitespace-nowrap truncate"><CheckCircle size={14} weight="fill" className="shrink-0" /> All changes saved</span>}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={applyToAll} disabled={applying || !silo} data-testid="v2-apply-all"
            title="Copies the zone/art choices you see here to EVERY silo you own. Global layer toggles (whitetext, bolden, dark-ignore) apply platform-wide already."
            className="text-[11px] px-2.5 py-2 rounded-lg bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1 disabled:opacity-40 whitespace-nowrap">
            <Copy size={13} /> {applying ? "Applying…" : "Save all silos"}
          </button>
          {dirty ? (
            <button
              onClick={save}
              disabled={!silo || saving}
              data-testid="v2-save"
              title="Persist the per-page zone/art changes to this silo only."
              className="text-sm px-3.5 py-2 rounded-lg bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <FloppyDisk size={15} weight="duotone" /> {saving ? "Saving…" : "Save"}
            </button>
          ) : (
            <span
              data-testid="v2-save"
              title="Zone/art changes are saved. Layer toggles (whitetext, bolden, dark-ignore) auto-save when you flip them."
              className="text-sm px-3.5 py-2 rounded-lg bg-[#E7EBE4] text-[#4A5D4E] flex items-center gap-1.5 whitespace-nowrap select-none"
            >
              <CheckCircle size={15} weight="fill" /> Saved
            </span>
          )}
        </div>
      </div>

      <div className="border hairline rounded-lg p-4 bg-[#FDFCF8]">
        <div className="flex items-center gap-2 mb-1">
          <Sparkle size={16} weight="duotone" className="text-[#4A5D4E]" />
          <h3 className="font-serif-display text-lg text-[#1C1B1A]">Elevated Mode (V2){silo ? ` — ${silo.name}` : ""}</h3>
        </div>
        <p className="text-xs text-[#6B6A68] max-w-xl">
          This silo's look. It dresses the pages whenever this silo is the leader — each silo can look different. Turn V2 on with the <strong>Silo art</strong> slider on your dashboard (slide fully right). Edits apply after you press <strong>Save</strong>.
        </p>
        {mode === "v2" && !anyFaceDressed && !canvasPainted && (
          <p className="mt-2.5 text-xs text-[#8A6A2F] bg-[#FFF8E6] border border-[#F0E0B8] rounded-md px-3 py-2 max-w-xl" data-testid="v2-no-art-notice">
            <strong>Elevated is on, but no cube face is dressed yet.</strong> Until this silo has a picture on a face (or a painted canvas), the white / black text effects stay asleep so your pages keep their normal, readable colours. Your settings are saved — they switch on by themselves the moment there's art to read them against.
          </p>
        )}
      </div>

      <ZoneControls title="Content canvas" zoneKey="page" zone={zones.page} onChange={(z) => setZone("page", z)} silo={silo} defaultFace={ZONE_DEFAULT_FACE.page} allowOff={true} />
      <ZoneControls title="Header band" zoneKey="band" zone={zones.band} onChange={(z) => setZone("band", z)} silo={silo} defaultFace={ZONE_DEFAULT_FACE.band} allowOff={true} />

      <div className="border hairline rounded-lg p-4 bg-[#FDFCF8]" data-testid="v2-leader">
        <h4 className="font-serif-display text-base text-[#1C1B1A] mb-1">Theme leader</h4>
        <p className="text-xs text-[#6B6A68] mb-3 max-w-xl">Which silo dresses the dashboard, marketing and account pages. Turn following off to lock it to one silo.</p>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setLeaderMode("dynamic")} data-testid="v2-leader-dynamic"
            className={`text-xs px-3 py-2 rounded-md border ${config.leader_mode === "dynamic" ? "bg-[#4A5D4E] text-white border-[#4A5D4E]" : "bg-white text-[#4A5D4E] border-transparent hover:border-[#A7B3A0]"}`}>
            Follow the last silo I entered
          </button>
          <span className="text-xs text-[#6B6A68]">or pin one:</span>
          <select value={config.leader_mode === "pinned" ? (config.leader_pin_silo_id || "") : ""}
            onChange={(e) => (e.target.value ? setPin(e.target.value) : setLeaderMode("dynamic"))}
            data-testid="v2-leader-pin" className="text-xs px-3 py-2 rounded-md border hairline bg-white">
            <option value="">— pick a silo —</option>
            {silos.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
          </select>
        </div>
        {leaderSilo && <p className="text-[11px] text-[#6B6A68] mt-2">Currently theming from: <strong>{leaderSilo.name}</strong></p>}
      </div>

      <div className="border hairline rounded-lg p-4 bg-[#FDFCF8]" data-testid="v2-bold">
        <h4 className="font-serif-display text-base text-[#1C1B1A] mb-1">Bolden loose text</h4>
        <p className="text-xs text-[#6B6A68] mb-3 max-w-xl">Text that isn't inside a box gets a heavier weight so it stays legible over the art — lets you keep the dim slider gentler. Usually only needed on phones, so you can turn it on for the whole site or just mobile.</p>
        {[
          { label: "Full site", on: !!config.bold_unboxed, toggle: toggleBold, tid: "v2-bold-toggle" },
          { label: "Mobile only", on: !!config.bold_unboxed_mobile, toggle: toggleBoldMobile, tid: "v2-bold-mobile-toggle" },
        ].map((row) => (
          <div key={row.tid} className="flex items-center justify-between py-1.5">
            <span className="text-sm text-[#1C1B1A]">{row.label}</span>
            <button onClick={row.toggle} role="switch" aria-checked={row.on} data-testid={row.tid}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${row.on ? "bg-[#4A5D4E]" : "bg-[#D8D5CD]"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${row.on ? "translate-x-4" : "translate-x-0.5"}`} />
            </button>
          </div>
        ))}
      </div>

      <div className="border hairline rounded-lg p-4 bg-[#FDFCF8]" data-testid="v2-blacktext">
        <h4 className="font-serif-display text-base text-[#1C1B1A] mb-1">All black text</h4>
        <p className="text-xs text-[#6B6A68] mb-3 max-w-xl">Force every bit of loose text on the content canvas to pure black — the same text the bold toggle touches (incl. Public Q&amp;A and the AI Assistant). Great for legibility over busy art. Text inside cards, bubbles and other features is left alone. <em className="not-italic text-[#8A8985]">Only active while V2 theming is on.</em></p>
        {[
          { k: "black_text", label: "Full site", on: !!config.black_text, toggle: toggleBlack },
          { k: "black_text_mobile", label: "Mobile only", on: !!config.black_text_mobile, toggle: toggleBlackMobile },
        ].map((row) => (
          <div key={row.k} className="flex items-center justify-between py-1.5">
            <span className="text-sm text-[#1C1B1A]">{row.label}</span>
            <button onClick={row.toggle} role="switch" aria-checked={row.on} data-testid={`v2-${row.k}-toggle`}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${row.on ? "bg-[#4A5D4E]" : "bg-[#D8D5CD]"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${row.on ? "translate-x-4" : "translate-x-0.5"}`} />
            </button>
          </div>
        ))}
      </div>

      {/* Feb 2026 (Randy pt-siloart-white-text) — mirror pair to the
          black-text card. Same explainer shape, same two sub-toggles,
          just for white. Mutually exclusive with black at the same
          scope (toggler enforces this). Both toggles are no-ops unless
          the V2 theming toggle at the top of this panel is on. */}
      <div className="border hairline rounded-lg p-4 bg-[#FDFCF8]" data-testid="v2-whitetext">
        <h4 className="font-serif-display text-base text-[#1C1B1A] mb-1">All white text</h4>
        <p className="text-xs text-[#6B6A68] mb-3 max-w-xl">Force every bit of loose text on the content canvas to pure white — the same text the bold toggle touches (incl. Public Q&amp;A and the AI Assistant). Great for legibility over busy art. Text inside cards, bubbles and other features is left alone. <em className="not-italic text-[#8A8985]">Only active while V2 theming is on. Turning this on clears the matching All-black-text option.</em></p>
        {[
          { k: "white_text", label: "Full site", on: !!config.white_text, toggle: toggleWhite },
          { k: "white_text_mobile", label: "Mobile only", on: !!config.white_text_mobile, toggle: toggleWhiteMobile },
        ].map((row) => (
          <div key={row.k} className="flex items-center justify-between py-1.5">
            <span className="text-sm text-[#1C1B1A]">{row.label}</span>
            <button onClick={row.toggle} role="switch" aria-checked={row.on} data-testid={`v2-${row.k}-toggle`}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${row.on ? "bg-[#4A5D4E]" : "bg-[#D8D5CD]"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${row.on ? "translate-x-4" : "translate-x-0.5"}`} />
            </button>
          </div>
        ))}
      </div>

      <div className="border hairline rounded-lg p-4 bg-[#FDFCF8] flex items-center justify-between gap-4" data-testid="v2-dark-ignore">
        <div>
          <h4 className="font-serif-display text-base text-[#1C1B1A]">Dark Mode ignore Content Canvas</h4>
          <p className="text-xs text-[#6B6A68] max-w-md mt-1">Keep the content-canvas art in its true colours when Dark Mode is on — everything else still darkens as normal, the art just isn't recoloured. Turn this off to let dark mode recolour the art too.</p>
        </div>
        <button onClick={toggleDarkIgnore} role="switch" aria-checked={config.dark_ignore_canvas !== false} data-testid="v2-dark-ignore-toggle"
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${config.dark_ignore_canvas !== false ? "bg-[#4A5D4E]" : "bg-[#D8D5CD]"}`}>
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${config.dark_ignore_canvas !== false ? "translate-x-4" : "translate-x-0.5"}`} />
        </button>
      </div>

      <div className="border hairline rounded-lg p-4 bg-[#FDFCF8]" data-testid="v2-pages">
        <h4 className="font-serif-display text-base text-[#1C1B1A] mb-1">Feature-page opt-out</h4>
        <p className="text-xs text-[#6B6A68] mb-3 max-w-xl">These pages have their own layout (response bubbles, side drawers, top header). V2 only dresses their content canvas — turn one off to leave that page fully plain. Each keeps its own dim slider that <strong>follows the content-canvas dim</strong> until you nudge it (then it holds its own value; hit <strong>Reset</strong> to re-sync).</p>
        {[
          { k: "assistant", label: "Advanced AI Assistant" },
          { k: "public_qa", label: "Public Q&A" },
        ].map((row) => {
          const master = zones.page.dim;
          const ov = zones.page_dim ? zones.page_dim[row.k] : null;
          const overridden = typeof ov === "number";
          const val = overridden ? ov : master;
          return (
            <div key={row.k} className="py-2 border-t hairline first:border-t-0">
              {/* Row 0 — the section header now stands on its own line
                  (Feb 17, 2026 pt-feature-page-v1-art), matching Randy's
                  art-upgrade sketch. The two toggle rows sit under it. */}
              <div className="text-sm text-[#1C1B1A] font-medium py-0.5">{row.label}</div>

              {/* Row 1 — MASTER toggle (existing behavior, renamed). When OFF,
                  the entire block below (including the V1 toggle + all the
                  DIM/ZOOM/TEXT sub-controls) collapses. */}
              <div className="flex items-center justify-between py-1">
                <span className="text-[12px] text-[#6B6A68] pr-3 leading-snug">
                  Allows Silo Art and Advanced settings into the {row.label}
                </span>
                <button onClick={() => togglePage(row.k)} role="switch" aria-checked={pageOn(row.k)} data-testid={`v2-page-${row.k}`}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${pageOn(row.k) ? "bg-[#4A5D4E]" : "bg-[#D8D5CD]"}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${pageOn(row.k) ? "translate-x-4" : "translate-x-0.5"}`} />
                </button>
              </div>

              {pageOn(row.k) && (() => {
                const fx = (zones.page_fx || {})[row.k] || {};
                const keepOff = fx.keep_art_off === true;
                return (
                  <>
                    {/* Row 2 — NEW (Feb 17, 2026 pt-feature-page-model-b).
                        Per-page kill switch. When ON, the feature page's
                        silo art is FORCED OFF regardless of what the
                        creator-dashboard slider says (V1 or V2). Default
                        OFF → follows the dashboard master. */}
                    <div className="flex items-center justify-between py-1">
                      <span className="text-[12px] text-[#6B6A68] pr-3 leading-snug">
                        Keep Art off regardless of V1 or V2 setting
                      </span>
                      <button onClick={() => setPageFx(row.k, { keep_art_off: !keepOff })} role="switch"
                        aria-checked={keepOff} data-testid={`v2-page-${row.k}-keepoff`}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${keepOff ? "bg-[#4A5D4E]" : "bg-[#D8D5CD]"}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${keepOff ? "translate-x-4" : "translate-x-0.5"}`} />
                      </button>
                    </div>

                    <div className="mt-1.5">
                      <div className="flex items-center justify-between text-[11px] mb-1">
                        <span className="uppercase tracking-[0.14em] text-[#6B6A68]">
                          Dim / readability{" "}
                          {overridden
                            ? <span className="normal-case tracking-normal text-[#C16E5A]">· custom</span>
                            : <span className="normal-case tracking-normal text-[#4A5D4E]">· follows canvas</span>}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="tabular-nums text-[#6B6A68]">{val}%</span>
                          {overridden && (
                            <button onClick={() => setPageDim(row.k, null)} data-testid={`v2-page-${row.k}-reset`}
                              className="text-[10px] px-1.5 py-0.5 rounded border hairline text-[#6B6A68] hover:text-[#4A5D4E] hover:border-[#4A5D4E]" title="Reset to match the content canvas">
                              Reset
                            </button>
                          )}
                        </div>
                      </div>
                      <input type="range" min="0" max="85" value={val} onChange={(e) => setPageDim(row.k, Number(e.target.value))}
                        data-testid={`v2-page-${row.k}-dim`} className="w-full accent-[#4A5D4E]" />

                      {/* Jul 2026 (Randy) — Art zoom (phones only). 100% = the full
                          cover fit (max stepped-back, zero edges — the canvas size
                          itself is the floor); higher = zoomed in. */}
                      {(() => {
                        const zoomSet = typeof fx.zoom === "number";
                        const zoomVal = zoomSet ? fx.zoom : 100;
                        const textSet = typeof fx.text_scale === "number";
                        const textVal = textSet ? fx.text_scale : 100;
                        const color = fx.text_color || "auto";
                        return (
                          <>
                            <div className="mt-3">
                              <div className="flex items-center justify-between text-[11px] mb-1">
                                <span className="uppercase tracking-[0.14em] text-[#6B6A68]">
                                  Art zoom · phones only{" "}
                                  {zoomSet
                                    ? <span className="normal-case tracking-normal text-[#C16E5A]">· custom</span>
                                    : <span className="normal-case tracking-normal text-[#4A5D4E]">· follows desktop framing</span>}
                                </span>
                                <div className="flex items-center gap-2">
                                  <span className="tabular-nums text-[#6B6A68]">{zoomVal}%</span>
                                  {zoomSet && (
                                    <button onClick={() => setPageFx(row.k, { zoom: null })} data-testid={`v2-page-${row.k}-zoom-reset`}
                                      className="text-[10px] px-1.5 py-0.5 rounded border hairline text-[#6B6A68] hover:text-[#4A5D4E] hover:border-[#4A5D4E]" title="Back to the desktop framing on phones too">
                                      Reset
                                    </button>
                                  )}
                                </div>
                              </div>
                              <input type="range" min="40" max="250" step="5" value={zoomVal}
                                onChange={(e) => setPageFx(row.k, { zoom: Number(e.target.value) })}
                                data-testid={`v2-page-${row.k}-zoom`} className="w-full accent-[#4A5D4E]" />
                              <div className="flex justify-between text-[9px] uppercase tracking-[0.14em] text-[#9b9a96] mt-0.5">
                                <span>Stepped back · blur-filled edges</span><span>Zoomed in</span>
                              </div>
                            </div>

                            <div className="mt-3">
                              <div className="flex items-center justify-between text-[11px] mb-1">
                                <span className="uppercase tracking-[0.14em] text-[#6B6A68]">Text size · whole group</span>
                                <div className="flex items-center gap-2">
                                  <span className="tabular-nums text-[#6B6A68]">{textVal}%</span>
                                  {textSet && (
                                    <button onClick={() => setPageFx(row.k, { text_scale: null })} data-testid={`v2-page-${row.k}-textscale-reset`}
                                      className="text-[10px] px-1.5 py-0.5 rounded border hairline text-[#6B6A68] hover:text-[#4A5D4E] hover:border-[#4A5D4E]" title="Back to the standard size">
                                      Reset
                                    </button>
                                  )}
                                </div>
                              </div>
                              <input type="range" min="10" max="140" step="5" value={textVal}
                                onChange={(e) => setPageFx(row.k, { text_scale: Number(e.target.value) })}
                                data-testid={`v2-page-${row.k}-textscale`} className="w-full accent-[#4A5D4E]" />
                              <div className="flex justify-between text-[9px] uppercase tracking-[0.14em] text-[#9b9a96] mt-0.5">
                                <span>Smaller</span><span>Bigger</span>
                              </div>
                            </div>

                            <div className="mt-3 flex items-center justify-between gap-3">
                              <span className="text-[11px] uppercase tracking-[0.14em] text-[#6B6A68]">Text colour</span>
                              <div className="flex rounded-md overflow-hidden border hairline text-[11px]">
                                {[["auto", "Auto"], ["black", "Black"], ["white", "White"]].map(([v, label]) => (
                                  <button key={v} onClick={() => setPageFx(row.k, { text_color: v === "auto" ? null : v })}
                                    data-testid={`v2-page-${row.k}-textcolor-${v}`}
                                    className={`px-3 py-1 ${color === v ? "bg-[#1C1B1A] text-[#FDFCF8]" : "bg-white text-[#6B6A68]"}`}>
                                    {label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="mt-2.5 flex items-center justify-between gap-3">
                              <span className="text-[11px] uppercase tracking-[0.14em] text-[#6B6A68]">Show the hero text</span>
                              <button onClick={() => setPageFx(row.k, { text_hidden: !fx.text_hidden })} role="switch"
                                aria-checked={!fx.text_hidden} data-testid={`v2-page-${row.k}-textshow`}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${!fx.text_hidden ? "bg-[#4A5D4E]" : "bg-[#D8D5CD]"}`}>
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${!fx.text_hidden ? "translate-x-4" : "translate-x-0.5"}`} />
                              </button>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </>
                );
              })()}
            </div>
          );
        })}
      </div>
    </div>
  );
}
